import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  CircleNotch,
  X,
  Warning,
  Buildings,
  Users,
  Briefcase,
  Gavel,
  File,
  HardDrives,
  ClockCounterClockwise,
  Prohibit,
  ArrowCounterClockwise,
  UsersThree,
  UserPlus,
  Key,
  CaretRight,
  EnvelopeSimple,
} from '@phosphor-icons/react';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';
import { toast } from 'sonner';
import { MIN_PASSWORD_LENGTH } from '@/lib/passwordPolicy';
import { ScrollLock } from '@/components/shared/ScrollLock';

// ── Types ─────────────────────────────────────────────────────────────────

interface TenantDetail {
  id: string;
  name: string;
  slug: string;
  legalName?: string;
  email?: string;
  phone?: string;
  status: string;
  plan: string;
  jurisdiction?: string;
  timezone?: string;
  currency?: string;
  trialEndsAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface TenantUsage {
  userCount: number;
  caseCount: number;
  hearingCount: number;
  documentCount: number;
  storageBytes: number;
}

interface LastLogin {
  lastLoginAt: string;
  email: string;
  firstName: string;
  lastName: string;
}

interface ChambersAdmin {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: string;
  lastLoginAt?: string;
  invitedAt?: string;
}

interface TenantDetailResponse {
  tenant: TenantDetail;
  usage: TenantUsage;
  lastLogin: LastLogin | null;
  chambersAdmin: ChambersAdmin | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────

function formatDate(str?: string) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(str?: string) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatStorage(bytes: number) {
  if (bytes < 1_048_576) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    active: 'bg-plat-success-soft text-plat-success-text',
    trial: 'bg-plat-warning-soft text-plat-warning-text',
    suspended: 'bg-plat-danger-soft text-plat-danger-text',
  };
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium capitalize ${map[status] ?? 'bg-plat-neutral-soft text-plat-secondary'}`}>
      {status}
    </span>
  );
}

function PlanBadge({ plan }: { plan: string }) {
  const map: Record<string, string> = {
    trial: 'bg-plat-warning-soft text-plat-warning-text',
    starter: 'bg-plat-info-soft text-plat-info-text',
    pro: 'bg-plat-indigo-soft text-plat-indigo-text',
    enterprise: 'bg-plat-purple-soft text-plat-purple-text',
  };
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium capitalize ${map[plan] ?? 'bg-plat-neutral-soft text-plat-secondary'}`}>
      {plan}
    </span>
  );
}

// ── Edit Modal ────────────────────────────────────────────────────────────

interface EditTenantModalProps {
  tenant: TenantDetail;
  onClose: () => void;
  onSuccess: () => void;
}

function EditTenantModal({ tenant, onClose, onSuccess }: EditTenantModalProps) {
  const [name, setName] = useState(tenant.name);
  const [email, setEmail] = useState(tenant.email ?? '');
  const [trialEndsAt, setTrialEndsAt] = useState(
    tenant.trialEndsAt ? new Date(tenant.trialEndsAt).toISOString().slice(0, 10) : ''
  );

  const mutation = useMutation({
    mutationFn: async () => {
      await platformApi.patch(`/tenants/${tenant.id}`, {
        name,
        email: email || undefined,
        trialEndsAt: trialEndsAt ? new Date(trialEndsAt).toISOString() : undefined,
      });
    },
    onSuccess: () => {
      toast.success('Organisation updated');
      onSuccess();
      onClose();
    },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/70 px-4">
      <ScrollLock />
      <div className="my-auto w-full max-w-md rounded-xl border border-plat-border bg-plat-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-plat-border px-6 py-4">
          <h2 className="text-[15px] font-semibold text-plat-fg">Edit Organisation</h2>
          <button onClick={onClose} className="text-plat-muted hover:text-plat-secondary"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Name *</label>
            <input required value={name} onChange={(e) => setName(e.target.value)}
              className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg focus:border-plat-focus focus:outline-none" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Contact email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg focus:border-plat-focus focus:outline-none" />
          </div>
          {tenant.plan === 'trial' && (
            <div>
              <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Trial ends at</label>
              <input type="date" value={trialEndsAt} onChange={(e) => setTrialEndsAt(e.target.value)}
                className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg focus:border-plat-focus focus:outline-none" />
            </div>
          )}
          <div className="flex justify-end gap-3 border-t border-plat-border pt-4">
            <button onClick={onClose} className="rounded-lg border border-plat-border px-4 py-2 text-sm text-plat-secondary hover:text-plat-fg">Cancel</button>
            <button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover disabled:bg-plat-neutral-soft disabled:cursor-not-allowed disabled:text-plat-disabled"
            >
              {mutation.isPending && <CircleNotch className="h-4 w-4 animate-spin" />}
              Save changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Extend Trial Modal ────────────────────────────────────────────────────

interface ExtendTrialModalProps {
  tenantId: string;
  onClose: () => void;
  onSuccess: () => void;
}

function ExtendTrialModal({ tenantId, onClose, onSuccess }: ExtendTrialModalProps) {
  const [newDate, setNewDate] = useState('');
  const mutation = useMutation({
    mutationFn: async () => {
      await platformApi.post(`/tenants/${tenantId}/extend-trial`, {
        newTrialEndsAt: new Date(newDate).toISOString(),
      });
    },
    onSuccess: () => { toast.success('Trial extended'); onSuccess(); onClose(); },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/70 px-4">
      <ScrollLock />
      <div className="my-auto w-full max-w-sm rounded-xl border border-plat-border bg-plat-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-plat-border px-6 py-4">
          <h2 className="text-[15px] font-semibold text-plat-fg">Extend Trial</h2>
          <button onClick={onClose} className="text-plat-muted hover:text-plat-secondary"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-plat-secondary">New trial end date *</label>
            <input
              type="date"
              required
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              min={new Date().toISOString().slice(0, 10)}
              className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg focus:border-plat-focus focus:outline-none"
            />
          </div>
          <div className="flex justify-end gap-3 border-t border-plat-border pt-4">
            <button onClick={onClose} className="rounded-lg border border-plat-border px-4 py-2 text-sm text-plat-secondary hover:text-plat-fg">Cancel</button>
            <button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || !newDate}
              className="inline-flex items-center gap-2 rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover disabled:bg-plat-neutral-soft disabled:cursor-not-allowed disabled:text-plat-disabled"
            >
              {mutation.isPending && <CircleNotch className="h-4 w-4 animate-spin" />}
              Extend Trial
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── TenantDetailPage ──────────────────────────────────────────────────────

// ── Change plan modal ─────────────────────────────────────────────────────

const PLAN_OPTIONS = ['trial', 'starter', 'pro', 'enterprise'] as const;

function ChangePlanModal({
  tenantId, currentPlan, onClose, onSuccess,
}: { tenantId: string; currentPlan: string; onClose: () => void; onSuccess: () => void }) {
  const [plan, setPlan] = useState(currentPlan);
  const [notes, setNotes] = useState('');

  const mutation = useMutation({
    mutationFn: () => platformApi.post(`/tenants/${tenantId}/plan`, { plan, notes: notes || undefined }),
    onSuccess: () => { toast.success('Plan changed — subscription recorded'); onSuccess(); onClose(); },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/70 px-4">
      <ScrollLock />
      <div className="my-auto w-full max-w-md rounded-xl border border-plat-border bg-plat-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-plat-border px-6 py-4">
          <h2 className="text-[15px] font-semibold text-plat-fg">Change plan</h2>
          <button onClick={onClose} className="text-plat-muted hover:text-plat-secondary"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4 px-6 py-5">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Plan</label>
            <select value={plan} onChange={(e) => setPlan(e.target.value)}
              className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm capitalize text-plat-fg focus:border-plat-focus focus:outline-none">
              {PLAN_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-plat-secondary">Notes (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder="e.g. Upgraded after demo call"
              className="w-full rounded-lg border border-plat-border bg-plat-bg p-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none" />
          </div>
          <p className="rounded-lg border border-plat-border bg-plat-bg px-3 py-2 text-xs text-plat-muted">
            This records a subscription change and updates the organisation's plan — it shows up in Billing.
          </p>
          <div className="flex justify-end gap-3 border-t border-plat-border pt-4">
            <button onClick={onClose} className="rounded-lg border border-plat-border px-4 py-2 text-sm text-plat-secondary hover:text-plat-fg">Cancel</button>
            <button onClick={() => mutation.mutate()} disabled={plan === currentPlan || mutation.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover disabled:bg-plat-neutral-soft disabled:cursor-not-allowed disabled:text-plat-disabled">
              {mutation.isPending && <CircleNotch className="h-4 w-4 animate-spin" />}
              Change plan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Members & Access (config/ops surface — people only, never legal data) ──

interface TenantMember {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  memberType: string;
  status: string;
  jobTitle: string | null;
  lastLoginAt: string | null;
  invitedAt: string | null;
  createdAt: string;
}

const prettyRole = (t: string) =>
  t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function MembersAccessSection({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<{ member: TenantMember; action: 'suspend' | 'reactivate' } | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [pwTarget, setPwTarget] = useState<TenantMember | null>(null);
  const [newPw, setNewPw] = useState('');

  const membersQ = useQuery({
    queryKey: ['platform', 'tenant', tenantId, 'users'],
    queryFn: async () => {
      const res = await platformApi.get(`/tenants/${tenantId}/users`);
      const data = res.data?.data ?? res.data;
      return data as { members: TenantMember[]; roleDistribution: Record<string, number> };
    },
    enabled: !!tenantId,
  });

  const syncQ = useQuery({
    queryKey: ['platform', 'tenant', tenantId, 'sync-health'],
    queryFn: async () => {
      const res = await platformApi.get(`/tenants/${tenantId}/sync-health`);
      const data = res.data?.data ?? res.data;
      return data as {
        mailboxes: { total: number; active: number; errored: number };
        calendar: { enabled: number; errored: number };
      };
    },
    enabled: !!tenantId,
  });

  const statusMutation = useMutation({
    mutationFn: ({ member, action }: { member: TenantMember; action: 'suspend' | 'reactivate' }) =>
      platformApi.post(`/tenants/${tenantId}/users/${member.id}/${action}`),
    onSuccess: (_res, vars) => {
      toast.success(vars.action === 'suspend' ? 'Member access suspended' : 'Member reactivated');
      setPending(null);
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenant', tenantId, 'users'] });
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenant', tenantId] });
    },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err)),
  });

  // Set a member's password directly, without leaving the tenant. Reuses the same platform
  // endpoint as the Users detail page (memberId IS the userId); governance is enforced server-side.
  const setPasswordMutation = useMutation({
    mutationFn: ({ member, password }: { member: TenantMember; password: string }) =>
      platformApi.post(`/users/${member.id}/set-password`, { password }),
    onSuccess: () => {
      toast.success('Password set — the member has been signed out and must use the new password');
      setPwTarget(null);
      setNewPw('');
    },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err, 'Failed to set password')),
  });

  // Resend the activation link to an invited member: rotates the token, emails it via Resend, and
  // returns the fresh link so we can also copy it to the clipboard (works even if email is delayed).
  const resendInviteMutation = useMutation({
    mutationFn: async (member: TenantMember) => {
      const res = await platformApi.post(`/users/${member.id}/resend-invite`, {});
      const data = res.data?.data ?? res.data;
      return data as { activationUrl?: string; inviteEmailStatus?: string };
    },
    onSuccess: async (data, member) => {
      let copied = false;
      if (data.activationUrl) {
        try { await navigator.clipboard.writeText(data.activationUrl); copied = true; } catch { /* clipboard blocked — email still sent */ }
      }
      toast.success(
        `Activation link emailed to ${member.email}${copied ? ' and copied to your clipboard' : ''}.`,
      );
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenant', tenantId, 'users'] });
    },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err, 'Failed to resend activation link')),
  });

  const members = membersQ.data?.members ?? [];
  const dist = membersQ.data?.roleDistribution ?? {};

  return (
    <div className="rounded-xl border border-plat-border bg-plat-surface p-5">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-plat-fg">
          <UsersThree className="h-4 w-4 text-plat-info-text" />
          Members &amp; Access
        </div>
        <button
          onClick={() => setAddOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-plat-border bg-plat-bg px-3 py-1.5 text-xs font-medium text-plat-neutral-text hover:bg-plat-hover"
        >
          <UserPlus className="h-3.5 w-3.5" /> Add member
        </button>
      </div>
      <p className="mb-4 text-xs text-plat-muted">
        Operational identity only — you can add a barrister or clerk, and gate a member's login here.
        Matters, fee notes and privileged notes are never exposed to the platform console.
      </p>

      {/* Integrations (email/calendar) sync health — connection state only */}
      {syncQ.data && (syncQ.data.mailboxes.total > 0 || syncQ.data.calendar.enabled > 0) && (
        <div className="mb-4 flex flex-wrap gap-2 text-xs">
          <span className="inline-flex items-center gap-1.5 rounded-md bg-plat-bg px-2.5 py-1 text-plat-secondary">
            Mailboxes
            <span className="rounded bg-plat-border px-1.5 text-[10px] font-semibold text-plat-secondary">
              {syncQ.data.mailboxes.total}
            </span>
          </span>
          {syncQ.data.mailboxes.errored > 0 ? (
            <span className="inline-flex items-center gap-1.5 rounded-md border border-plat-danger-line bg-plat-danger-soft px-2.5 py-1 text-plat-danger-text">
              <Warning className="h-3.5 w-3.5" weight="fill" />
              {syncQ.data.mailboxes.errored} sync error{syncQ.data.mailboxes.errored > 1 ? 's' : ''}
            </span>
          ) : (
            syncQ.data.mailboxes.total > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-md border border-plat-success-line bg-plat-success-soft px-2.5 py-1 text-plat-success-text">
                Email sync healthy
              </span>
            )
          )}
          {syncQ.data.calendar.enabled > 0 && (
            <span
              className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 ${
                syncQ.data.calendar.errored > 0
                  ? 'border border-plat-danger-line bg-plat-danger-soft text-plat-danger-text'
                  : 'border border-plat-success-line bg-plat-success-soft text-plat-success-text'
              }`}
            >
              Calendar sync ({syncQ.data.calendar.enabled})
              {syncQ.data.calendar.errored > 0 && ` · ${syncQ.data.calendar.errored} error`}
            </span>
          )}
        </div>
      )}

      {/* Role distribution */}
      {Object.keys(dist).length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {Object.entries(dist)
            .sort((a, b) => b[1] - a[1])
            .map(([role, count]) => (
              <span
                key={role}
                className="inline-flex items-center gap-1.5 rounded-md bg-plat-bg px-2.5 py-1 text-xs text-plat-secondary"
              >
                {prettyRole(role)}
                <span className="rounded bg-plat-border px-1.5 text-[10px] font-semibold text-plat-secondary">{count}</span>
              </span>
            ))}
        </div>
      )}

      {membersQ.isLoading ? (
        <div className="flex items-center justify-center py-8">
          <CircleNotch className="h-5 w-5 animate-spin text-plat-info-text" />
        </div>
      ) : membersQ.error ? (
        <div className="text-sm text-plat-danger-text">{getPlatformApiErrorMessage(membersQ.error)}</div>
      ) : members.length === 0 ? (
        <div className="text-sm text-plat-muted">No members yet.</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-plat-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-plat-bg text-left text-[11px] uppercase tracking-wider text-plat-muted">
                <th className="px-3 py-2 font-medium">Member</th>
                <th className="px-3 py-2 font-medium">Role</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Last login</th>
                <th className="px-3 py-2 text-right font-medium">Access</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-plat-border">
              {members.map((m) => {
                const canSuspend = m.status === 'active' || m.status === 'invited';
                const canReactivate = m.status === 'suspended' || m.status === 'inactive';
                const busy = statusMutation.isPending && statusMutation.variables?.member.id === m.id;
                return (
                  <tr key={m.id} className="text-plat-secondary">
                    <td className="px-3 py-2.5">
                      {/* Deep-link to the full user detail page — the complete account toolkit
                          (reset MFA, force logout, resend invite, transfer, delete) lives there. */}
                      <Link to={`/platform/users/${m.id}`} className="group inline-flex items-center gap-1">
                        <span className="font-medium text-plat-fg group-hover:text-plat-info-text group-hover:underline">
                          {m.firstName} {m.lastName}
                        </span>
                        <CaretRight className="h-3 w-3 text-plat-faint group-hover:text-plat-info-text" />
                      </Link>
                      <div className="text-xs text-plat-muted">{m.email}</div>
                    </td>
                    <td className="px-3 py-2.5">{prettyRole(m.memberType)}</td>
                    <td className="px-3 py-2.5">
                      <StatusBadge status={m.status} />
                    </td>
                    <td className="px-3 py-2.5 text-xs text-plat-secondary">{formatDateTime(m.lastLoginAt ?? undefined)}</td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                      {m.status === 'invited' && (
                        <button
                          onClick={() => resendInviteMutation.mutate(m)}
                          disabled={resendInviteMutation.isPending && resendInviteMutation.variables?.id === m.id}
                          className="inline-flex items-center gap-1.5 rounded-md border border-plat-border px-2.5 py-1 text-xs font-medium text-plat-secondary hover:bg-plat-hover disabled:cursor-not-allowed disabled:text-plat-disabled"
                          title="Email the activation link (and copy it to share)"
                        >
                          {resendInviteMutation.isPending && resendInviteMutation.variables?.id === m.id
                            ? <CircleNotch className="h-3.5 w-3.5 animate-spin" />
                            : <EnvelopeSimple className="h-3.5 w-3.5" />}
                          Resend link
                        </button>
                      )}
                      <button
                        onClick={() => { setPwTarget(m); setNewPw(''); }}
                        className="inline-flex items-center gap-1.5 rounded-md border border-plat-border px-2.5 py-1 text-xs font-medium text-plat-secondary hover:bg-plat-hover"
                      >
                        <Key className="h-3.5 w-3.5" />
                        Set password
                      </button>
                      {canSuspend && (
                        <button
                          onClick={() => setPending({ member: m, action: 'suspend' })}
                          disabled={busy}
                          className="inline-flex items-center gap-1.5 rounded-md border border-plat-danger-line px-2.5 py-1 text-xs font-medium text-plat-danger-text hover:bg-plat-danger-soft disabled:cursor-not-allowed disabled:text-plat-disabled"
                        >
                          {busy ? <CircleNotch className="h-3.5 w-3.5 animate-spin" /> : <Prohibit className="h-3.5 w-3.5" />}
                          Suspend
                        </button>
                      )}
                      {canReactivate && (
                        <button
                          onClick={() => statusMutation.mutate({ member: m, action: 'reactivate' })}
                          disabled={busy}
                          className="inline-flex items-center gap-1.5 rounded-md border border-plat-success-line px-2.5 py-1 text-xs font-medium text-plat-success-text hover:bg-plat-success-soft disabled:cursor-not-allowed disabled:text-plat-disabled"
                        >
                          {busy ? <CircleNotch className="h-3.5 w-3.5 animate-spin" /> : <ArrowCounterClockwise className="h-3.5 w-3.5" />}
                          Reactivate
                        </button>
                      )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {addOpen && (
        <AddMemberModal
          tenantId={tenantId}
          tenantName={tenantName}
          onClose={() => setAddOpen(false)}
          onAdded={() => {
            queryClient.invalidateQueries({ queryKey: ['platform', 'tenant', tenantId, 'users'] });
            queryClient.invalidateQueries({ queryKey: ['platform', 'tenant', tenantId] });
          }}
        />
      )}

      {/* Set-password modal — operator types the new password directly, without leaving the tenant */}
      {pwTarget && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/70 px-4">
          <ScrollLock />
          <div className="my-auto w-full max-w-md rounded-xl border border-plat-border bg-plat-surface p-6 shadow-2xl">
            <h3 className="flex items-center gap-2 text-[15px] font-semibold text-plat-fg">
              <Key className="h-4 w-4 text-plat-info-text" /> Set password for {pwTarget.firstName} {pwTarget.lastName}
            </h3>
            <p className="mt-2 text-sm text-plat-secondary">
              Sets this member's password immediately — including a chambers administrator. They are
              signed out of every device and must sign in again with the new password. Share it over a
              secure channel; they can change it themselves afterwards.
            </p>
            <input
              type="text"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              placeholder={`New password (min ${MIN_PASSWORD_LENGTH} characters)`}
              autoComplete="new-password"
              className="mt-3 h-9 w-full rounded-lg border border-plat-border bg-plat-sunken px-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setPwTarget(null)}
                disabled={setPasswordMutation.isPending}
                className="rounded-lg border border-plat-border px-4 py-2 text-sm text-plat-secondary hover:bg-plat-hover disabled:cursor-not-allowed disabled:text-plat-disabled"
              >
                Cancel
              </button>
              <button
                onClick={() => setPasswordMutation.mutate({ member: pwTarget, password: newPw })}
                disabled={newPw.length < MIN_PASSWORD_LENGTH || setPasswordMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover disabled:bg-plat-neutral-soft disabled:cursor-not-allowed disabled:text-plat-disabled"
              >
                {setPasswordMutation.isPending ? <CircleNotch className="h-4 w-4 animate-spin" /> : <Key className="h-4 w-4" />}
                Set password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suspend confirm — extra caution when the target is the chambers admin */}
      {pending?.action === 'suspend' && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/70 px-4">
          <ScrollLock />
          <div className="my-auto w-full max-w-sm rounded-xl border border-plat-border bg-plat-surface p-6 shadow-2xl">
            <div className="mb-3 flex items-center gap-3">
              <Warning className="h-6 w-6 text-plat-danger-text" weight="fill" />
              <h3 className="text-[15px] font-semibold text-plat-fg">Suspend this member?</h3>
            </div>
            <p className="mb-3 text-sm text-plat-secondary">
              <strong className="text-plat-fg">
                {pending.member.firstName} {pending.member.lastName}
              </strong>{' '}
              at {tenantName} will be logged out immediately and blocked from signing in. This can be reversed.
            </p>
            {pending.member.memberType === 'admin' && (
              <p className="mb-4 rounded-lg border border-plat-warning-line bg-plat-warning-soft px-3 py-2 text-xs text-plat-warning-text">
                This is a chambers admin. Suspending them may leave the chambers without an administrator.
              </p>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setPending(null)}
                className="flex-1 rounded-lg border border-plat-border py-2 text-sm text-plat-secondary hover:text-plat-fg"
              >
                Cancel
              </button>
              <button
                onClick={() => statusMutation.mutate(pending)}
                disabled={statusMutation.isPending}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-plat-danger-solid py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-danger-solid-hover disabled:bg-plat-neutral-soft disabled:cursor-not-allowed disabled:text-plat-disabled"
              >
                {statusMutation.isPending && <CircleNotch className="h-4 w-4 animate-spin" />}
                Suspend
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Add a barrister or clerk directly into this chambers (reuses the audited POST /admin/users —
// the same endpoint as Users → New User). Provisioning/support action; no content access.
const MEMBER_ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: 'silk', label: 'KC (Silk)' },
  { value: 'junior', label: 'Junior Barrister' },
  { value: 'pupil', label: 'Pupil' },
  { value: 'senior_clerk', label: 'Senior Clerk' },
  { value: 'junior_clerk', label: 'Junior Clerk' },
];

function AddMemberModal({
  tenantId, tenantName, onClose, onAdded,
}: { tenantId: string; tenantName: string; onClose: () => void; onAdded: () => void }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [memberType, setMemberType] = useState('junior');

  const mutation = useMutation({
    mutationFn: () =>
      platformApi.post('/users', {
        chambersId: tenantId,
        memberType,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        ...(jobTitle.trim() ? { jobTitle: jobTitle.trim() } : {}),
      }),
    onSuccess: (res) => {
      const status = res.data?.data?.inviteEmailStatus;
      toast.success(
        status === 'sent'
          ? `${firstName} added — an activation invite was emailed.`
          : `${firstName} added to ${tenantName}.`,
      );
      onAdded();
      onClose();
    },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err)),
  });

  const valid = firstName.trim() && lastName.trim() && /.+@.+\..+/.test(email.trim());

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/70 px-4">
      <ScrollLock />
      <div className="my-auto w-full max-w-md rounded-xl border border-plat-border bg-plat-surface p-6 shadow-2xl">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-[15px] font-semibold text-plat-fg">
            <UserPlus className="h-4 w-4 text-plat-info-text" /> Add member to {tenantName}
          </h3>
          <button onClick={onClose} className="text-plat-muted hover:text-plat-secondary"><X className="h-4 w-4" /></button>
        </div>
        <p className="mb-4 text-xs text-plat-muted">
          Creates a barrister or clerk in this chambers and emails them an activation invite. A
          provisioning action — recorded in the platform audit log. Grants no access to legal content.
        </p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-plat-secondary">First name</label>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-neutral-text focus:border-plat-focus focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-plat-secondary">Last name</label>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-neutral-text focus:border-plat-focus focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-plat-secondary">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-neutral-text focus:border-plat-focus focus:outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-plat-secondary">Role</label>
              <select value={memberType} onChange={(e) => setMemberType(e.target.value)} className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-neutral-text focus:border-plat-focus focus:outline-none">
                {MEMBER_ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-plat-secondary">Job title <span className="text-plat-faint">(optional)</span></label>
              <input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} className="h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-neutral-text focus:border-plat-focus focus:outline-none" />
            </div>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-plat-border px-4 py-2 text-sm text-plat-secondary hover:bg-plat-hover">Cancel</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!valid || mutation.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover disabled:bg-plat-neutral-soft disabled:cursor-not-allowed disabled:text-plat-disabled"
          >
            {mutation.isPending ? <CircleNotch className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            Add member
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showEdit, setShowEdit] = useState(false);
  const [showExtendTrial, setShowExtendTrial] = useState(false);
  const [showSuspendConfirm, setShowSuspendConfirm] = useState(false);
  const [showChangePlan, setShowChangePlan] = useState(false);

  const storageQ = useQuery({
    queryKey: ['platform', 'tenant', id, 'storage'],
    queryFn: async () => {
      const res = await platformApi.get(`/tenants/${id}/storage`);
      return (res.data?.data ?? res.data) as {
        documentCount: number; totalBytes: number; totalMb: number;
        byCategory: { category: string; count: number; bytes: number }[];
      };
    },
    enabled: !!id,
  });

  const { data, isLoading, error } = useQuery<TenantDetailResponse>({
    queryKey: ['platform', 'tenant', id],
    queryFn: async () => {
      const res = await platformApi.get(`/tenants/${id}`);
      return res.data?.data ?? res.data;
    },
    enabled: !!id,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['platform', 'tenant', id] });
    queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
  };

  const suspendMutation = useMutation({
    mutationFn: () => platformApi.post(`/tenants/${id}/suspend`),
    onSuccess: () => { toast.success('Organisation suspended'); setShowSuspendConfirm(false); invalidate(); },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err)),
  });

  const activateMutation = useMutation({
    mutationFn: () => platformApi.post(`/tenants/${id}/activate`),
    onSuccess: () => { toast.success('Organisation reactivated'); invalidate(); },
    onError: (err) => toast.error(getPlatformApiErrorMessage(err)),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <CircleNotch className="h-7 w-7 animate-spin text-plat-info-text" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-xl border border-plat-danger-line bg-plat-danger-soft p-6 text-sm text-plat-danger-text">
        {getPlatformApiErrorMessage(error ?? new Error('Unknown error'))}
      </div>
    );
  }

  const { tenant, usage, lastLogin, chambersAdmin } = data;

  return (
    <div className="space-y-5">
      {/* Back */}
      <button
        onClick={() => navigate('/platform/tenants')}
        className="inline-flex items-center gap-1.5 text-sm text-plat-muted hover:text-plat-secondary"
      >
        <ArrowLeft className="h-4 w-4" />
        Tenants
      </button>

      {/* Header */}
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-plat-fg">{tenant.name}</h2>
            <StatusBadge status={tenant.status} />
            <PlanBadge plan={tenant.plan} />
          </div>
          <div className="mt-1 text-xs text-plat-muted">{tenant.slug}</div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2">
          {tenant.status === 'active' && (
            <button
              onClick={() => setShowSuspendConfirm(true)}
              className="rounded-lg border border-plat-danger-line px-4 py-2 text-sm font-medium text-plat-danger-text hover:bg-plat-danger-soft"
            >
              Suspend
            </button>
          )}
          {tenant.status === 'suspended' && (
            <button
              onClick={() => activateMutation.mutate()}
              disabled={activateMutation.isPending}
              className="inline-flex items-center gap-2 rounded-lg border border-plat-success-line px-4 py-2 text-sm font-medium text-plat-success-text hover:bg-plat-success-soft disabled:cursor-not-allowed disabled:text-plat-disabled"
            >
              {activateMutation.isPending && <CircleNotch className="h-4 w-4 animate-spin" />}
              Reactivate
            </button>
          )}
          {tenant.status === 'trial' && (
            <button
              onClick={() => setShowExtendTrial(true)}
              className="rounded-lg border border-plat-info-line px-4 py-2 text-sm font-medium text-plat-info-text hover:bg-plat-info-soft"
            >
              Extend Trial
            </button>
          )}
          <button
            onClick={() => setShowChangePlan(true)}
            className="rounded-lg border border-plat-border px-4 py-2 text-sm font-medium text-plat-secondary hover:bg-plat-hover"
          >
            Change plan
          </button>
          <button
            onClick={() => setShowEdit(true)}
            className="rounded-lg bg-plat-border px-4 py-2 text-sm font-medium text-plat-secondary hover:bg-plat-border-strong"
          >
            Edit
          </button>
        </div>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Organisation details */}
        <div className="rounded-xl border border-plat-border bg-plat-surface p-5">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-plat-fg">
            <Buildings className="h-4 w-4 text-plat-info-text" />
            Organisation Details
          </div>
          <dl className="space-y-3 text-sm">
            {[
              { label: 'Legal name', value: tenant.legalName },
              { label: 'Contact email', value: tenant.email },
              { label: 'Phone', value: tenant.phone },
              { label: 'Jurisdiction', value: tenant.jurisdiction },
              { label: 'Timezone', value: tenant.timezone },
              { label: 'Currency', value: tenant.currency },
              { label: 'Created', value: formatDate(tenant.createdAt) },
              { label: 'Trial ends', value: formatDate(tenant.trialEndsAt) },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-start justify-between gap-4">
                <dt className="text-plat-muted shrink-0">{label}</dt>
                <dd className="text-right text-plat-neutral-text">{value ?? <span className="text-plat-faint">—</span>}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Usage stats */}
        <div className="rounded-xl border border-plat-border bg-plat-surface p-5">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-plat-fg">
            <HardDrives className="h-4 w-4 text-plat-indigo-text" />
            Usage
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: <Users className="h-5 w-5 text-plat-info-text" />, label: 'Users', value: usage.userCount },
              { icon: <Briefcase className="h-5 w-5 text-plat-indigo-text" />, label: 'Matters', value: usage.caseCount },
              { icon: <Gavel className="h-5 w-5 text-plat-purple-text" />, label: 'Hearings', value: usage.hearingCount },
              { icon: <File className="h-5 w-5 text-plat-success-text" />, label: 'Documents', value: usage.documentCount },
            ].map(({ icon, label, value }) => (
              <div key={label} className="rounded-lg bg-plat-bg p-3">
                <div className="mb-1">{icon}</div>
                <div className="text-[10px] uppercase tracking-wider text-plat-muted">{label}</div>
                <div className="text-[20px] font-bold text-plat-fg">{value}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-lg bg-plat-bg px-3 py-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-plat-muted">Storage</span>
              <span className="font-medium text-plat-neutral-text">{formatStorage(usage.storageBytes)}</span>
            </div>
            {(storageQ.data?.byCategory?.length ?? 0) > 0 && (
              <div className="mt-2 space-y-1 border-t border-plat-border pt-2">
                {storageQ.data!.byCategory
                  .slice()
                  .sort((a, b) => b.bytes - a.bytes)
                  .map((c) => (
                    <div key={c.category} className="flex items-center justify-between text-xs">
                      <span className="capitalize text-plat-muted">
                        {c.category.replace(/_/g, ' ')} <span className="text-plat-faint">· {c.count}</span>
                      </span>
                      <span className="text-plat-secondary">{formatStorage(c.bytes)}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom cards */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Chambers admin */}
        <div className="rounded-xl border border-plat-border bg-plat-surface p-5">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-plat-fg">
            <Users className="h-4 w-4 text-plat-success-text" />
            Chambers Admin
          </div>
          {chambersAdmin ? (
            <dl className="space-y-3 text-sm">
              {[
                { label: 'Name', value: `${chambersAdmin.firstName} ${chambersAdmin.lastName}` },
                { label: 'Email', value: chambersAdmin.email },
                { label: 'Status', value: <StatusBadge status={chambersAdmin.status} /> },
                { label: 'Last login', value: formatDateTime(chambersAdmin.lastLoginAt) },
                { label: 'Invited at', value: formatDate(chambersAdmin.invitedAt) },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-start justify-between gap-4">
                  <dt className="text-plat-muted shrink-0">{label}</dt>
                  <dd className="text-right text-plat-neutral-text">{value}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <div className="text-sm text-plat-muted">No admin account assigned.</div>
          )}
        </div>

        {/* Last activity */}
        <div className="rounded-xl border border-plat-border bg-plat-surface p-5">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-plat-fg">
            <ClockCounterClockwise className="h-4 w-4 text-plat-warning-text" />
            Last Activity
          </div>
          {lastLogin ? (
            <dl className="space-y-3 text-sm">
              {[
                { label: 'User', value: `${lastLogin.firstName} ${lastLogin.lastName}` },
                { label: 'Email', value: lastLogin.email },
                { label: 'Last login at', value: formatDateTime(lastLogin.lastLoginAt) },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-start justify-between gap-4">
                  <dt className="text-plat-muted shrink-0">{label}</dt>
                  <dd className="text-right text-plat-neutral-text">{value}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <div className="text-sm text-plat-muted">No logins recorded yet.</div>
          )}
        </div>
      </div>

      {/* Members & Access — config/ops surface */}
      <MembersAccessSection tenantId={tenant.id} tenantName={tenant.name} />

      {/* Suspend confirm */}
      {showSuspendConfirm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/70 px-4">
          <ScrollLock />
          <div className="my-auto w-full max-w-sm rounded-xl border border-plat-border bg-plat-surface p-6 shadow-2xl">
            <div className="mb-3 flex items-center gap-3">
              <Warning className="h-6 w-6 text-plat-danger-text" weight="fill" />
              <h3 className="text-[15px] font-semibold text-plat-fg">Suspend organisation?</h3>
            </div>
            <p className="mb-5 text-sm text-plat-secondary">
              All users at <strong className="text-plat-fg">{tenant.name}</strong> will immediately lose access. This can be reversed.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowSuspendConfirm(false)}
                className="flex-1 rounded-lg border border-plat-border py-2 text-sm text-plat-secondary hover:text-plat-fg"
              >
                Cancel
              </button>
              <button
                onClick={() => suspendMutation.mutate()}
                disabled={suspendMutation.isPending}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-plat-danger-solid py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-danger-solid-hover disabled:bg-plat-neutral-soft disabled:cursor-not-allowed disabled:text-plat-disabled"
              >
                {suspendMutation.isPending && <CircleNotch className="h-4 w-4 animate-spin" />}
                Suspend
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showEdit && (
        <EditTenantModal
          tenant={tenant}
          onClose={() => setShowEdit(false)}
          onSuccess={invalidate}
        />
      )}
      {showExtendTrial && (
        <ExtendTrialModal
          tenantId={tenant.id}
          onClose={() => setShowExtendTrial(false)}
          onSuccess={invalidate}
        />
      )}
      {showChangePlan && (
        <ChangePlanModal
          tenantId={tenant.id}
          currentPlan={tenant.plan}
          onClose={() => setShowChangePlan(false)}
          onSuccess={() => {
            invalidate();
            queryClient.invalidateQueries({ queryKey: ['platform', 'subscriptions'] });
          }}
        />
      )}
    </div>
  );
}
